import { DriversPageContext } from "layouts/card-holder/context/useCardHolderContext.ts"
import { filterDrivers, filterVehicles } from "layouts/card-holder/utils/functions"
import { stableSort } from "lib/utils.ts"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { SubmitHandler } from "react-hook-form"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import {
    AccountInfo,
    DriverInfo,
    IFormFilterDriver,
    VehicleInfo,
} from "types/list-card-holder"
import { SORTORDER, SortOrder } from "types/sort.ts"

// Type guards to make raw data handling safer and more TypeScript-friendly
const isDriverOrVehicle = (val: unknown): val is AccountInfo => {
    if (typeof val !== "object" || val === null) return false
    const v = val as { type?: unknown; info?: unknown }
    return (
        (v.type === "driver" || v.type === "vehicle") &&
        typeof v.info !== "undefined"
    )
}

const isDriverOrVehicleArray = (input: unknown): input is AccountInfo[] => {
    return Array.isArray(input) && input.every(isDriverOrVehicle)
}

export const CardHolderPageProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const { cardHolderService } = useMmsService()
    const { useGetListCardHolders } = cardHolderService

    const {
        data: rawListDrivers,
        success: apiSuccess,
        isLoading: isRefetchingList,
        sendRequest: fetchListDrivers,
    } = useGetListCardHolders()

    const [listDrivers, setListDrivers] = useState<DriverInfo[]>([])
    const [listVehicles, setListVehicles] = useState<VehicleInfo[]>([])

    const [isLoading, setIsLoading] = useState(true)
    const [isVehicle, setIsVehicle] = useState(false)

    const defaultFilterValues = useRef<IFormFilterDriver>({
        firstName: "",
        lastName: "",
        email: "",
        licensePlate: "",
        description: "",
        status: "",
    }).current
    const [filterValues, setFilterValues] = useState<IFormFilterDriver | null>(
        null
    )
    const [filteredDrivers, setFilteredDrivers] = useState<
        DriverInfo[] | VehicleInfo[]
    >([])
    const [noResultFilter, setNoResultFilter] = useState(false)

    const [sortedDrivers, setSortedDrivers] = useState<
        DriverInfo[] | VehicleInfo[]
    >([])
    const [sortOrder, setSortOrder] = useState<SortOrder>(SORTORDER.ASC)
    const [orderByDriver, setOrderByDriver] =
        useState<keyof DriverInfo>("lastName")
    const [orderByVehicle, setOrderByVehicle] =
        useState<keyof VehicleInfo>("licensePlate")

    const [openAddCardHolder, setOpenAddCardHolder] = useState(false)
    const [openUpdateCardHolder, setOpenUpdateCardHolder] = useState(false)
    const [openActivateCardHolder, setOpenActivateCardHolder] = useState(false)
    const [openDeactivateCardHolder, setOpenDeactivateCardHolder] = useState(false)

    const [driverSelected, setDriverSelected] = useState<
        null | DriverInfo | VehicleInfo
    >(null)

    // id of the card-holder being updated: while the GET /v2/card-holders
    // refetch triggered after the update is not finished, its row shows a
    // loader instead of the MoreVertIcon.
    const [updatedCardHolderId, setUpdatedCardHolderId] = useState<
        string | null
    >(null)

    const [pageNumber, setPageNumber] = useState(1)
    const [numberOfPages, setNumberOfPages] = useState(0)
    const rowsPerPage = useMemo(() => 10, [])

    const currentRows = useMemo(() => {
        return sortedDrivers.slice(
            rowsPerPage * (pageNumber - 1),
            rowsPerPage * pageNumber
        )
    }, [sortedDrivers, pageNumber, rowsPerPage])



    useEffect(() => {
        setPageNumber(1)
    }, [filterValues])

    useEffect(() => {
        setNumberOfPages(Math.ceil(sortedDrivers.length / rowsPerPage))
    }, [rowsPerPage, sortedDrivers])

    const handleChangePage = useCallback(
        (_event: React.ChangeEvent<unknown>, page: number) => {
            setPageNumber(page)
            setDriverSelected(null)
        },
        []
    )

    // cela permet d'effectuer la requête lorsque le composant se monte pour la première fois
    useEffect(() => {
        fetchListDrivers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (rawListDrivers == null) return
        if (isDriverOrVehicleArray(rawListDrivers)) {
            const drivers = rawListDrivers
                .filter((item) => item.type === "driver")
                .map((item) => item.info as DriverInfo)

            const vehicles = rawListDrivers
                .filter((item) => item.type === "vehicle")
                .map((item) => {
                    const info = item.info as VehicleInfo
                    return {
                        ...info,
                        autocharge: item.vid,
                    } as VehicleInfo
                })

            setListDrivers(drivers)
            setListVehicles(vehicles)
        } else if (Array.isArray(rawListDrivers)) {
            // Fallback: service returns plain list of drivers
            setListDrivers(rawListDrivers as DriverInfo[])
            setListVehicles([])
        } else {
            setListDrivers([])
            setListVehicles([])
        }

        setIsLoading(false)
        // The refreshed data has arrived: remove the loader from the updated
        // card-holder's row and exit the "selected" state.
        setUpdatedCardHolderId((prev) => {
            if (prev) setDriverSelected(null)
            return null
        })
    }, [rawListDrivers])

    useEffect(() => {
        if (isVehicle) {
            if (!filterValues) {
                setFilteredDrivers(listVehicles)
            } else {
                const filterFn = filterVehicles(filterValues)
                const filteredVehiclesResult = listVehicles.filter(filterFn)
                setFilteredDrivers(filteredVehiclesResult)
            }
        } else {
            if (!filterValues) {
                setFilteredDrivers(listDrivers)
            } else {
                const filterFn = filterDrivers(filterValues)
                const filteredDriversResult = listDrivers.filter(filterFn)
                setFilteredDrivers(filteredDriversResult)
            }
        }
    }, [listDrivers, filterValues, listVehicles])

    const onFilterSubmit: SubmitHandler<IFormFilterDriver> = useCallback(
        (filterValuesForm) => {
            if (
                JSON.stringify(filterValuesForm) !==
                JSON.stringify(defaultFilterValues)
            ) {
                setFilterValues(trimFilterValuesForm(filterValuesForm))
            } else {
                setFilterValues(null)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )
    const trimFilterValuesForm = (filterValuesForm: IFormFilterDriver) => {
        return {
            firstName: filterValuesForm.firstName.trim(),
            lastName: filterValuesForm.lastName.trim(),
            email: filterValuesForm.email.trim(),
            licensePlate: filterValuesForm.licensePlate.trim(),
            description: filterValuesForm.description.trim(),
            status: filterValuesForm.status,
        }
    }

    useEffect(() => {
        if (isVehicle) {
            const sortFn = stableSort<VehicleInfo>(orderByVehicle, sortOrder)
            const sortedVehiclesResult = [
                ...(filteredDrivers as VehicleInfo[]),
            ].sort(sortFn)
            setSortedDrivers(sortedVehiclesResult)
        } else {
            const sortFn = stableSort<DriverInfo>(orderByDriver, sortOrder)
            const sortedDriversResult = [
                ...(filteredDrivers as DriverInfo[]),
            ].sort(sortFn)
            setSortedDrivers(sortedDriversResult)
        }
    }, [filteredDrivers, sortOrder, orderByVehicle, orderByDriver])

    useEffect(() => {
        if (filteredDrivers.length === 0 && filterValues) {
            setNoResultFilter(true)
        } else {
            setNoResultFilter(false)
        }
    }, [filteredDrivers, filterValues])

    const onClosePopup = useCallback(() => {
        setOpenAddCardHolder(false)
        setOpenUpdateCardHolder(false)
        setOpenActivateCardHolder(false)
        setOpenDeactivateCardHolder(false)
        fetchListDrivers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <DriversPageContext.Provider
            value={{
                apiSuccess,
                listDrivers,
                listVehicles,
                filteredDrivers,
                sortedDrivers,
                sortOrder,
                orderByDriver,
                orderByVehicle,
                isLoading,
                isRefetchingList,
                isVehicle,
                openAddCardHolder,
                openUpdateCardHolder,
                openActivateCardHolder,
                openDeactivateCardHolder,
                setOpenAddCardHolder,
                setOpenUpdateCardHolder,
                setOpenActivateCardHolder,
                setOpenDeactivateCardHolder,
                onClosePopup,
                onFilterSubmit,
                defaultFilterValues,
                filterValues,
                setSortOrder,
                setOrderByDriver,
                setOrderByVehicle,
                fetchListDrivers,
                driverSelected,
                setDriverSelected,
                updatedCardHolderId,
                setUpdatedCardHolderId,
                setIsVehicle,
                pageNumber,
                numberOfPages,
                currentRows,
                handleChangePage,
                noResultFilter,
            }}
        >
            {children}
        </DriversPageContext.Provider>
    )
}
